// src/contexts/MessagingContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import {
  Conversation,
  Message,
  Group,
  GroupMessage,
  MessagingState,
  SendMessagePayload,
  WSMessagesReadEvent,
  WSGroupMessagesReadEvent,
  WSUserOnlineEvent,
} from '../types/messaging';
import messagingService from '../services/messagingService';
import websocketService from '../services/websocketService';
import { useAuth } from '../hooks/useAuth';

// ========== Context Types ==========
interface MessagingContextType {
  // State
  state: MessagingState;

  // Conversations
  fetchConversations: () => Promise<void>;
  selectConversation: (conversation: Conversation | null) => void;
  createConversation: (otherUserId: number) => Promise<Conversation>;
  deleteConversation: (conversationId: number) => Promise<void>;

  // Messages
  fetchMessages: (conversationId: number, beforeId?: number) => Promise<boolean>;
  sendMessage: (conversationId: number, content: string, replyToId?: number) => Promise<void>;
  sendMediaMessage: (conversationId: number, file: File, type: 'image' | 'file' | 'audio', replyToId?: number) => Promise<void>;
  editMessage: (conversationId: number, messageId: number, content: string) => Promise<void>;
  deleteMessage: (conversationId: number, messageId: number) => Promise<void>;
  markAsRead: (conversationId: number) => Promise<void>;
  addReaction: (conversationId: number, messageId: number, reaction: string) => Promise<void>;
  removeReaction: (conversationId: number, messageId: number) => Promise<void>;

  // Groups
  fetchGroups: () => Promise<void>;
  selectGroup: (group: Group | null) => void;
  createGroup: (name: string, description: string, memberIds: number[]) => Promise<Group>;
  updateGroup: (groupId: number, name: string, description?: string) => Promise<void>;
  deleteGroup: (groupId: number) => Promise<void>;
  leaveGroup: (groupId: number) => Promise<void>;
  addGroupMember: (groupId: number, userId: number) => Promise<void>;
  removeGroupMember: (groupId: number, userId: number) => Promise<void>;

  // Group Messages
  fetchGroupMessages: (groupId: number, beforeId?: number) => Promise<boolean>;
  sendGroupMessage: (groupId: number, content: string, replyToId?: number) => Promise<void>;
  sendGroupMediaMessage: (groupId: number, file: File, type: 'image' | 'file' | 'audio', replyToId?: number) => Promise<void>;
  editGroupMessage: (groupId: number, messageId: number, content: string) => Promise<void>;
  deleteGroupMessage: (groupId: number, messageId: number) => Promise<void>;
  markGroupAsRead: (groupId: number) => Promise<void>;
  addGroupReaction: (groupId: number, messageId: number, reaction: string) => Promise<void>;
  removeGroupReaction: (groupId: number, messageId: number) => Promise<void>;

  // Typing
  sendTyping: (conversationId?: number, groupId?: number, isTyping?: boolean) => void;

  // UI State
  setActiveTab: (tab: 'private' | 'groups') => void;
  setSearchQuery: (query: string) => void;
  searchUsers: (query: string) => Promise<void>;

  // Contacts
  fetchContacts: () => Promise<void>;
  fetchContactRequests: () => Promise<void>;
  sendContactRequest: (toUserId: number) => Promise<void>;
  acceptContactRequest: (requestId: number) => Promise<void>;
  rejectContactRequest: (requestId: number) => Promise<void>;

  // Connection
  connect: () => void;
  disconnect: () => void;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

// ========== Provider Component ==========
interface MessagingProviderProps {
  children: ReactNode;
}

const initialState: MessagingState = {
  conversations: [],
  currentConversation: null,
  messages: {},
  groups: [],
  currentGroup: null,
  groupMessages: {},
  typingUsers: {},
  onlineUsers: new Set(),
  unreadCount: 0,
  totalGroupUnread: 0,
  contacts: [],
  contactRequests: [],
  activeTab: 'private',
  isLoadingConversations: false,
  isLoadingMessages: false,
  isLoadingGroups: false,
  wsConnected: false,
  searchQuery: '',
  searchResults: [],
};

export const MessagingProvider: React.FC<MessagingProviderProps> = ({ children }) => {
  const { authState } = useAuth();
  const [state, setState] = useState<MessagingState>(initialState);
  const typingTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Track locally read conversations/groups to preserve read state on refetch
  const locallyReadConversationsRef = useRef<Set<number>>(new Set());
  const locallyReadGroupsRef = useRef<Set<number>>(new Set());

  // Track if we've already set up WebSocket (persists across renders)
  const wsInitializedRef = useRef(false);
  const prevUserIdRef = useRef<number | null>(null);

  // ========== WebSocket Connection ==========
  const connect = useCallback(() => {
    if (authState.user?.id) {
      console.log('[MessagingContext] Connecting WebSocket...');
      websocketService.connect(authState.user.id);
    }
  }, [authState.user?.id]);

  const disconnect = useCallback(() => {
    console.log('[MessagingContext] Disconnecting WebSocket...');
    websocketService.disconnect();
  }, []);

  // ========== WebSocket Connection Effect ==========
  // Connect when user authenticates, disconnect only when user changes or logs out
  useEffect(() => {
    const userId = authState.user?.id;
    const isAuthenticated = authState.isAuthenticated;

    console.log('[MessagingContext] Auth effect running:', {
      isAuthenticated,
      userId,
      wsInitialized: wsInitializedRef.current,
      prevUserId: prevUserIdRef.current,
    });

    // User logged in or changed
    if (isAuthenticated && userId) {
      // Only connect if not already connected for this user
      if (!wsInitializedRef.current || prevUserIdRef.current !== userId) {
        console.log('[MessagingContext] Connecting WebSocket for user:', userId);
        wsInitializedRef.current = true;
        prevUserIdRef.current = userId;
        websocketService.connect(userId);
      } else {
        console.log('[MessagingContext] WebSocket already initialized, skipping connect');
      }
    }

    // User logged out
    if (!isAuthenticated && wsInitializedRef.current) {
      console.log('[MessagingContext] User logged out - disconnecting WebSocket');
      wsInitializedRef.current = false;
      prevUserIdRef.current = null;
      websocketService.disconnect();
    }

    // NO CLEANUP FUNCTION - WebSocket only disconnects when user logs out (handled above)
    // Browser handles cleanup on page unload automatically
  }, [authState.isAuthenticated, authState.user?.id]);

  // ========== WebSocket Event Subscriptions ==========
  // Separate effect for subscriptions - runs once on mount
  useEffect(() => {
    // Connection status listener
    const unsubStatus = websocketService.onConnectionStatusChange((status) => {
      console.log('[MessagingContext] WebSocket status:', status);
      setState((prev) => ({
        ...prev,
        wsConnected: status === 'connected',
      }));
    });

    // New DM message
    const unsubNewMessage = websocketService.on('new_message', (event: any) => {
      console.log('[MessagingContext] new_message event:', event);

      const conversation_id = event.conversation_id;
      if (!conversation_id) {
        console.error('[MessagingContext] new_message missing conversation_id');
        return;
      }

      const messageType = event.type || 'text';

      const message: Message = {
        id: event.id,
        conversation_id: event.conversation_id,
        sender_id: event.sender_id,
        content: event.content,
        type: messageType,
        status: 'delivered',
        created_at: event.created_at,
      };

      console.log('[MessagingContext] Constructed message:', message);

      setState((prev) => {
        const conversationMessages = prev.messages[conversation_id] || [];
        const messageExists = conversationMessages.some((m) => m.id === message.id);

        const updatedConversations = prev.conversations.map((conv) => {
          if (conv.id === conversation_id) {
            const isCurrentConv = prev.currentConversation?.id === conversation_id;
            return {
              ...conv,
              last_message: message,
              unread_count: isCurrentConv ? 0 : conv.unread_count + 1,
            };
          }
          return conv;
        });

        const newUnreadCount = updatedConversations.reduce((acc, conv) => acc + conv.unread_count, 0);

        return {
          ...prev,
          messages: {
            ...prev.messages,
            [conversation_id]: messageExists
              ? conversationMessages
              : [...conversationMessages, message],
          },
          conversations: updatedConversations,
          unreadCount: newUnreadCount,
        };
      });
    });

    // New group message
    const unsubNewGroupMessage = websocketService.on('new_group_message', (event: any) => {
      console.log('[MessagingContext] new_group_message event:', event);

      const group_id = event.group_id;
      if (!group_id) {
        console.error('[MessagingContext] new_group_message missing group_id');
        return;
      }

      const messageType = event.type || 'text';

      const message: GroupMessage = {
        id: event.id,
        group_id: event.group_id,
        sender_id: event.sender_id,
        content: event.content,
        type: messageType,
        created_at: event.created_at,
      };

      console.log('[MessagingContext] Constructed group message:', message);

      setState((prev) => {
        const groupMessages = prev.groupMessages[group_id] || [];
        const messageExists = groupMessages.some((m) => m.id === message.id);

        const updatedGroups = prev.groups.map((group) => {
          if (group.id === group_id) {
            const isCurrentGroup = prev.currentGroup?.id === group_id;
            return {
              ...group,
              last_message: message,
              unread_count: isCurrentGroup ? 0 : (group.unread_count || 0) + 1,
            };
          }
          return group;
        });

        const newTotalGroupUnread = updatedGroups.reduce((acc, g) => acc + (g.unread_count || 0), 0);

        return {
          ...prev,
          groupMessages: {
            ...prev.groupMessages,
            [group_id]: messageExists ? groupMessages : [...groupMessages, message],
          },
          groups: updatedGroups,
          totalGroupUnread: newTotalGroupUnread,
        };
      });
    });

    // Messages read (DM)
    const unsubMessagesRead = websocketService.on('messages_read', (event: WSMessagesReadEvent) => {
      const { conversation_id, reader_id } = event;
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversation_id]: (prev.messages[conversation_id] || []).map((msg) => ({
            ...msg,
            status: msg.sender_id !== reader_id ? 'read' : msg.status,
          })),
        },
      }));
    });

    // Group messages read
    const unsubGroupMessagesRead = websocketService.on('group_messages_read', (event: WSGroupMessagesReadEvent) => {
      const { group_id } = event;
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [group_id]: (prev.groupMessages[group_id] || []).map((msg) => ({
            ...msg,
            read_by_count: (msg.read_by_count || 0) + 1,
          })),
        },
      }));
    });

    // User typing
    const unsubUserTyping = websocketService.on('user_typing', (event: any) => {
      console.log('[MessagingContext] user_typing event:', event);

      const { conversation_id, group_id, user_id, is_typing } = event;
      const key = conversation_id ? `conv_${conversation_id}` : `group_${group_id}`;

      setState((prev) => {
        const currentTyping = prev.typingUsers[key] || [];
        let newTyping: number[];

        if (is_typing) {
          newTyping = currentTyping.includes(user_id) ? currentTyping : [...currentTyping, user_id];
        } else {
          newTyping = currentTyping.filter((id) => id !== user_id);
        }

        return {
          ...prev,
          typingUsers: {
            ...prev.typingUsers,
            [key]: newTyping,
          },
        };
      });

      // Clear typing indicator after 5 seconds
      const timeoutKey = `${key}_${user_id}`;
      if (typingTimeoutRef.current[timeoutKey]) {
        clearTimeout(typingTimeoutRef.current[timeoutKey]);
      }
      if (is_typing) {
        typingTimeoutRef.current[timeoutKey] = setTimeout(() => {
          setState((prev) => {
            const currentTyping = prev.typingUsers[key] || [];
            return {
              ...prev,
              typingUsers: {
                ...prev.typingUsers,
                [key]: currentTyping.filter((id) => id !== user_id),
              },
            };
          });
        }, 5000);
      }
    });

    // User online status
    const unsubUserOnline = websocketService.on('user_online', (event: WSUserOnlineEvent) => {
      const { user_id, is_online } = event;
      setState((prev) => {
        const newOnlineUsers = new Set(prev.onlineUsers);
        if (is_online) {
          newOnlineUsers.add(user_id);
        } else {
          newOnlineUsers.delete(user_id);
        }
        return {
          ...prev,
          onlineUsers: newOnlineUsers,
        };
      });
    });

    // Group update - just update state, don't refetch
    const unsubGroupUpdate = websocketService.on('group_update', (event: any) => {
      console.log('[MessagingContext] group_update event:', event);
      // We could update the specific group here instead of refetching all
    });

    // Cleanup subscriptions on unmount only
    return () => {
      unsubStatus();
      unsubNewMessage();
      unsubNewGroupMessage();
      unsubMessagesRead();
      unsubGroupMessagesRead();
      unsubUserTyping();
      unsubUserOnline();
      unsubGroupUpdate();

      // Clear all typing timeouts
      const timeouts = typingTimeoutRef.current;
      Object.values(timeouts).forEach(clearTimeout);
      typingTimeoutRef.current = {};
    };
  }, []); // Empty deps - run once on mount

  // ========== Conversations ==========
  const fetchConversations = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoadingConversations: true }));
    try {
      const conversations = await messagingService.getConversations();

      // Preserve locally read state - if we marked a conversation as read locally,
      // keep it at 0 even if the server hasn't synced yet
      const conversationsWithLocalReadState = conversations.map((conv) => {
        if (locallyReadConversationsRef.current.has(conv.id)) {
          return { ...conv, unread_count: 0 };
        }
        return conv;
      });

      const unreadCount = conversationsWithLocalReadState.reduce((acc, conv) => acc + conv.unread_count, 0);
      setState((prev) => ({
        ...prev,
        conversations: conversationsWithLocalReadState,
        unreadCount,
        isLoadingConversations: false,
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch conversations:', error);
      setState((prev) => ({ ...prev, isLoadingConversations: false }));
    }
  }, []);

  const selectConversation = useCallback((conversation: Conversation | null) => {
    setState((prev) => {
      // If selecting a conversation with unread messages, clear the badge immediately (optimistic)
      let updatedConversations = prev.conversations;
      let newUnreadCount = prev.unreadCount;

      if (conversation && conversation.unread_count > 0) {
        updatedConversations = prev.conversations.map((conv) =>
          conv.id === conversation.id ? { ...conv, unread_count: 0 } : conv
        );
        newUnreadCount = updatedConversations.reduce((acc, conv) => acc + conv.unread_count, 0);
      }

      return {
        ...prev,
        currentConversation: conversation,
        currentGroup: null, // Deselect group when selecting conversation
        conversations: updatedConversations,
        unreadCount: newUnreadCount,
      };
    });

    if (conversation) {
      fetchMessages(conversation.id);
      // Call markAsRead via REST API (works even without WebSocket)
      markAsRead(conversation.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createConversation = useCallback(async (otherUserId: number): Promise<Conversation> => {
    const conversation = await messagingService.createConversation({ other_user_id: otherUserId });
    setState((prev) => {
      const exists = prev.conversations.some((c) => c.id === conversation.id);
      return {
        ...prev,
        conversations: exists ? prev.conversations : [conversation, ...prev.conversations],
        currentConversation: conversation,
        currentGroup: null, // Deselect any group
      };
    });
    // Fetch messages for the new conversation
    try {
      const response = await messagingService.getMessages(conversation.id, 50);
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversation.id]: response.messages,
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch messages for new conversation:', error);
    }
    return conversation;
  }, []);

  const deleteConversation = useCallback(async (conversationId: number) => {
    await messagingService.deleteConversation(conversationId);
    setState((prev) => ({
      ...prev,
      conversations: prev.conversations.filter((c) => c.id !== conversationId),
      currentConversation: prev.currentConversation?.id === conversationId ? null : prev.currentConversation,
    }));
  }, []);

  // ========== Messages ==========
  const fetchMessages = useCallback(async (conversationId: number, beforeId?: number): Promise<boolean> => {
    setState((prev) => ({ ...prev, isLoadingMessages: true }));
    try {
      const response = await messagingService.getMessages(conversationId, 50, beforeId);
      setState((prev) => {
        const existingMessages = beforeId ? (prev.messages[conversationId] || []) : [];
        // Prepend older messages when paginating
        const newMessages = beforeId
          ? [...response.messages, ...existingMessages]
          : response.messages;

        return {
          ...prev,
          messages: {
            ...prev.messages,
            [conversationId]: newMessages,
          },
          isLoadingMessages: false,
        };
      });
      return response.has_more;
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch messages:', error);
      setState((prev) => ({ ...prev, isLoadingMessages: false }));
      return false;
    }
  }, []);

  const sendMessage = useCallback(async (conversationId: number, content: string, replyToId?: number) => {
    const tempId = `temp_${Date.now()}`;
    const messageId = -Date.now();

    // Add optimistic message (find reply_to inside setState to get current state)
    setState((prev) => {
      // Find the original message if replying
      let replyTo: Message | null = null;
      if (replyToId) {
        const conversationMessages = prev.messages[conversationId] || [];
        replyTo = conversationMessages.find(m => m.id === replyToId) || null;
      }

      const optimisticMessage: Message = {
        id: messageId,
        conversation_id: conversationId,
        sender_id: authState.user?.id || 0,
        content,
        type: 'text',
        status: 'sending',
        created_at: new Date().toISOString(),
        reply_to_id: replyToId,
        reply_to: replyTo,
        _tempId: tempId,
        _isOptimistic: true,
      };

      return {
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: [...(prev.messages[conversationId] || []), optimisticMessage],
        },
      };
    });

    try {
      const payload: SendMessagePayload = { content };
      if (replyToId) {
        payload.reply_to_id = replyToId;
      }
      const message = await messagingService.sendMessage(conversationId, payload);

      // Replace optimistic message with real message, preserving reply_to
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).map((m) =>
            m._tempId === tempId ? { ...message, reply_to: m.reply_to || message.reply_to } : m
          ),
        },
        conversations: prev.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, last_message: message } : conv
        ),
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to send message:', error);
      // Mark optimistic message as failed
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).map((m) =>
            m._tempId === tempId ? { ...m, status: 'failed' } : m
          ),
        },
      }));
    }
  }, [authState.user?.id]);

  const sendMediaMessage = useCallback(async (
    conversationId: number,
    file: File,
    type: 'image' | 'file' | 'audio',
    replyToId?: number
  ) => {
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage: Message = {
      id: -Date.now(),
      conversation_id: conversationId,
      sender_id: authState.user?.id || 0,
      content: file.name,
      type,
      status: 'sending',
      created_at: new Date().toISOString(),
      file_name: file.name,
      file_size: file.size,
      reply_to_id: replyToId,
      _tempId: tempId,
      _isOptimistic: true,
    };

    setState((prev) => ({
      ...prev,
      messages: {
        ...prev.messages,
        [conversationId]: [...(prev.messages[conversationId] || []), optimisticMessage],
      },
    }));

    try {
      const message = await messagingService.uploadMessageMedia(conversationId, file, type, replyToId);
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).map((m) =>
            m._tempId === tempId ? message : m
          ),
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to send media:', error);
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).map((m) =>
            m._tempId === tempId ? { ...m, status: 'failed' } : m
          ),
        },
      }));
    }
  }, [authState.user?.id]);

  const editMessage = useCallback(async (conversationId: number, messageId: number, content: string) => {
    const updatedMessage = await messagingService.editMessage(conversationId, messageId, content);
    setState((prev) => ({
      ...prev,
      messages: {
        ...prev.messages,
        [conversationId]: (prev.messages[conversationId] || []).map((m) =>
          m.id === messageId ? updatedMessage : m
        ),
      },
    }));
  }, []);

  const deleteMessage = useCallback(async (conversationId: number, messageId: number) => {
    await messagingService.deleteMessage(conversationId, messageId);
    setState((prev) => ({
      ...prev,
      messages: {
        ...prev.messages,
        [conversationId]: (prev.messages[conversationId] || []).map((m) =>
          m.id === messageId ? { ...m, deleted_at: new Date().toISOString() } : m
        ),
      },
    }));
  }, []);

  const markAsRead = useCallback(async (conversationId: number) => {
    // Track locally that this conversation has been read
    // This prevents refetch from restoring unread badges
    locallyReadConversationsRef.current.add(conversationId);

    // Update state immediately (optimistic)
    setState((prev) => {
      const updatedConversations = prev.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      );
      const newUnreadCount = updatedConversations.reduce((acc, conv) => acc + conv.unread_count, 0);
      return {
        ...prev,
        conversations: updatedConversations,
        unreadCount: newUnreadCount,
      };
    });

    // Call API in background - don't wait for it
    try {
      await messagingService.markMessagesAsRead(conversationId);
    } catch (error) {
      console.error('[MessagingContext] Failed to mark as read:', error);
      // Even if API fails, keep local state (user has seen the messages)
    }
  }, []);

  // ========== Reactions (DM) ==========
  const addReaction = useCallback(async (conversationId: number, messageId: number, reaction: string) => {
    try {
      await messagingService.addReaction(conversationId, messageId, { reaction });
      // Optimistically update the state
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).map((m) => {
            if (m.id === messageId) {
              const existingReactions = m.reactions || [];
              // Remove any existing reaction from this user
              const filteredReactions = existingReactions.filter(
                (r) => r.user_id !== authState.user?.id
              );
              return {
                ...m,
                reactions: [
                  ...filteredReactions,
                  {
                    id: Date.now(), // Temporary ID
                    message_id: messageId,
                    user_id: authState.user?.id || 0,
                    reaction,
                    created_at: new Date().toISOString(),
                  },
                ],
              };
            }
            return m;
          }),
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to add reaction:', error);
    }
  }, [authState.user?.id]);

  const removeReaction = useCallback(async (conversationId: number, messageId: number) => {
    try {
      await messagingService.removeReaction(conversationId, messageId);
      // Optimistically update the state
      setState((prev) => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).map((m) => {
            if (m.id === messageId) {
              return {
                ...m,
                reactions: (m.reactions || []).filter(
                  (r) => r.user_id !== authState.user?.id
                ),
              };
            }
            return m;
          }),
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to remove reaction:', error);
    }
  }, [authState.user?.id]);

  // ========== Groups ==========
  const fetchGroups = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoadingGroups: true }));
    try {
      const groups = await messagingService.getGroups();

      // Preserve locally read state
      const groupsWithLocalReadState = groups.map((group) => {
        if (locallyReadGroupsRef.current.has(group.id)) {
          return { ...group, unread_count: 0 };
        }
        return group;
      });

      const totalGroupUnread = groupsWithLocalReadState.reduce((acc, g) => acc + (g.unread_count || 0), 0);
      setState((prev) => ({
        ...prev,
        groups: groupsWithLocalReadState,
        totalGroupUnread,
        isLoadingGroups: false,
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch groups:', error);
      setState((prev) => ({ ...prev, isLoadingGroups: false }));
    }
  }, []);

  const selectGroup = useCallback((group: Group | null) => {
    setState((prev) => {
      // If selecting a group with unread messages, clear the badge immediately (optimistic)
      let updatedGroups = prev.groups;
      let newTotalGroupUnread = prev.totalGroupUnread;

      if (group && (group.unread_count || 0) > 0) {
        updatedGroups = prev.groups.map((g) =>
          g.id === group.id ? { ...g, unread_count: 0 } : g
        );
        newTotalGroupUnread = updatedGroups.reduce((acc, g) => acc + (g.unread_count || 0), 0);
      }

      return {
        ...prev,
        currentGroup: group,
        currentConversation: null, // Deselect conversation when selecting group
        groups: updatedGroups,
        totalGroupUnread: newTotalGroupUnread,
      };
    });

    if (group) {
      fetchGroupMessages(group.id);
      // Call markGroupAsRead via REST API (works even without WebSocket)
      markGroupAsRead(group.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createGroup = useCallback(async (name: string, description: string, memberIds: number[]): Promise<Group> => {
    const group = await messagingService.createGroup({ name, description, member_ids: memberIds });
    setState((prev) => ({
      ...prev,
      groups: [group, ...prev.groups],
    }));
    return group;
  }, []);

  const updateGroup = useCallback(async (groupId: number, name: string, description?: string) => {
    const updatedGroup = await messagingService.updateGroup(groupId, { name, description });
    setState((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => (g.id === groupId ? updatedGroup : g)),
      currentGroup: prev.currentGroup?.id === groupId ? updatedGroup : prev.currentGroup,
    }));
  }, []);

  const deleteGroup = useCallback(async (groupId: number) => {
    await messagingService.deleteGroup(groupId);
    setState((prev) => ({
      ...prev,
      groups: prev.groups.filter((g) => g.id !== groupId),
      currentGroup: prev.currentGroup?.id === groupId ? null : prev.currentGroup,
    }));
  }, []);

  const leaveGroup = useCallback(async (groupId: number) => {
    await messagingService.leaveGroup(groupId);
    setState((prev) => ({
      ...prev,
      groups: prev.groups.filter((g) => g.id !== groupId),
      currentGroup: prev.currentGroup?.id === groupId ? null : prev.currentGroup,
    }));
  }, []);

  const addGroupMember = useCallback(async (groupId: number, userId: number) => {
    await messagingService.addGroupMember(groupId, { user_id: userId });
    // Refetch group details
    const group = await messagingService.getGroup(groupId);
    setState((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => (g.id === groupId ? group : g)),
      currentGroup: prev.currentGroup?.id === groupId ? group : prev.currentGroup,
    }));
  }, []);

  const removeGroupMember = useCallback(async (groupId: number, userId: number) => {
    await messagingService.removeGroupMember(groupId, userId);
    const group = await messagingService.getGroup(groupId);
    setState((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => (g.id === groupId ? group : g)),
      currentGroup: prev.currentGroup?.id === groupId ? group : prev.currentGroup,
    }));
  }, []);

  // ========== Group Messages ==========
  const fetchGroupMessages = useCallback(async (groupId: number, beforeId?: number): Promise<boolean> => {
    setState((prev) => ({ ...prev, isLoadingMessages: true }));
    try {
      const response = await messagingService.getGroupMessages(groupId, 50, beforeId);
      setState((prev) => {
        const existingMessages = beforeId ? (prev.groupMessages[groupId] || []) : [];
        const newMessages = beforeId
          ? [...response.messages, ...existingMessages]
          : response.messages;

        return {
          ...prev,
          groupMessages: {
            ...prev.groupMessages,
            [groupId]: newMessages,
          },
          isLoadingMessages: false,
        };
      });
      return response.has_more;
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch group messages:', error);
      setState((prev) => ({ ...prev, isLoadingMessages: false }));
      return false;
    }
  }, []);

  const sendGroupMessage = useCallback(async (groupId: number, content: string, replyToId?: number) => {
    const tempId = `temp_${Date.now()}`;
    const messageId = -Date.now();

    // Add optimistic message (find reply_to inside setState to get current state)
    setState((prev) => {
      let replyTo: GroupMessage | null = null;
      if (replyToId) {
        const groupMsgs = prev.groupMessages[groupId] || [];
        replyTo = groupMsgs.find(m => m.id === replyToId) || null;
      }

      const optimisticMessage: GroupMessage = {
        id: messageId,
        group_id: groupId,
        sender_id: authState.user?.id || 0,
        content,
        type: 'text',
        created_at: new Date().toISOString(),
        reply_to_id: replyToId,
        reply_to: replyTo,
        _tempId: tempId,
        _isOptimistic: true,
      };

      return {
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: [...(prev.groupMessages[groupId] || []), optimisticMessage],
        },
      };
    });

    try {
      const payload: SendMessagePayload = { content };
      if (replyToId) {
        payload.reply_to_id = replyToId;
      }
      const message = await messagingService.sendGroupMessage(groupId, payload);

      // Replace optimistic message with real message, preserving reply_to
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: (prev.groupMessages[groupId] || []).map((m) =>
            m._tempId === tempId ? { ...message, reply_to: m.reply_to || message.reply_to } : m
          ),
        },
        groups: prev.groups.map((g) =>
          g.id === groupId ? { ...g, last_message: message } : g
        ),
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to send group message:', error);
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: (prev.groupMessages[groupId] || []).filter((m) => m._tempId !== tempId),
        },
      }));
    }
  }, [authState.user?.id]);

  const sendGroupMediaMessage = useCallback(async (
    groupId: number,
    file: File,
    type: 'image' | 'file' | 'audio',
    replyToId?: number
  ) => {
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage: GroupMessage = {
      id: -Date.now(),
      group_id: groupId,
      sender_id: authState.user?.id || 0,
      content: file.name,
      type,
      created_at: new Date().toISOString(),
      file_name: file.name,
      file_size: file.size,
      reply_to_id: replyToId,
      _tempId: tempId,
      _isOptimistic: true,
    };

    setState((prev) => ({
      ...prev,
      groupMessages: {
        ...prev.groupMessages,
        [groupId]: [...(prev.groupMessages[groupId] || []), optimisticMessage],
      },
    }));

    try {
      const message = await messagingService.uploadGroupMessageMedia(groupId, file, type, replyToId);
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: (prev.groupMessages[groupId] || []).map((m) =>
            m._tempId === tempId ? message : m
          ),
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to send group media:', error);
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: (prev.groupMessages[groupId] || []).filter((m) => m._tempId !== tempId),
        },
      }));
    }
  }, [authState.user?.id]);

  const markGroupAsRead = useCallback(async (groupId: number) => {
    // Track locally that this group has been read
    locallyReadGroupsRef.current.add(groupId);

    // Update state immediately (optimistic)
    setState((prev) => {
      const updatedGroups = prev.groups.map((g) =>
        g.id === groupId ? { ...g, unread_count: 0 } : g
      );
      const newTotalGroupUnread = updatedGroups.reduce((acc, g) => acc + (g.unread_count || 0), 0);
      return {
        ...prev,
        groups: updatedGroups,
        totalGroupUnread: newTotalGroupUnread,
      };
    });

    // Call API in background
    try {
      await messagingService.markGroupMessagesAsRead(groupId);
    } catch (error) {
      console.error('[MessagingContext] Failed to mark group as read:', error);
      // Keep local state even if API fails
    }
  }, []);

  const editGroupMessage = useCallback(async (groupId: number, messageId: number, content: string) => {
    const updatedMessage = await messagingService.editGroupMessage(groupId, messageId, content);
    setState((prev) => ({
      ...prev,
      groupMessages: {
        ...prev.groupMessages,
        [groupId]: (prev.groupMessages[groupId] || []).map((m) =>
          m.id === messageId ? updatedMessage : m
        ),
      },
    }));
  }, []);

  const deleteGroupMessage = useCallback(async (groupId: number, messageId: number) => {
    await messagingService.deleteGroupMessage(groupId, messageId);
    setState((prev) => ({
      ...prev,
      groupMessages: {
        ...prev.groupMessages,
        [groupId]: (prev.groupMessages[groupId] || []).map((m) =>
          m.id === messageId ? { ...m, deleted_at: new Date().toISOString() } : m
        ),
      },
    }));
  }, []);

  // ========== Reactions (Group) ==========
  const addGroupReaction = useCallback(async (groupId: number, messageId: number, reaction: string) => {
    try {
      await messagingService.addGroupMessageReaction(groupId, messageId, { reaction });
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: (prev.groupMessages[groupId] || []).map((m) => {
            if (m.id === messageId) {
              const existingReactions = m.reactions || [];
              const filteredReactions = existingReactions.filter(
                (r) => r.user_id !== authState.user?.id
              );
              return {
                ...m,
                reactions: [
                  ...filteredReactions,
                  {
                    id: Date.now(),
                    message_id: messageId,
                    user_id: authState.user?.id || 0,
                    reaction,
                    created_at: new Date().toISOString(),
                  },
                ],
              };
            }
            return m;
          }),
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to add group reaction:', error);
    }
  }, [authState.user?.id]);

  const removeGroupReaction = useCallback(async (groupId: number, messageId: number) => {
    try {
      await messagingService.removeGroupMessageReaction(groupId, messageId);
      setState((prev) => ({
        ...prev,
        groupMessages: {
          ...prev.groupMessages,
          [groupId]: (prev.groupMessages[groupId] || []).map((m) => {
            if (m.id === messageId) {
              return {
                ...m,
                reactions: (m.reactions || []).filter(
                  (r) => r.user_id !== authState.user?.id
                ),
              };
            }
            return m;
          }),
        },
      }));
    } catch (error) {
      console.error('[MessagingContext] Failed to remove group reaction:', error);
    }
  }, [authState.user?.id]);

  // ========== Typing ==========
  const sendTyping = useCallback((conversationId?: number, groupId?: number, isTyping: boolean = true) => {
    websocketService.sendTyping(conversationId, groupId, isTyping);
  }, []);

  // ========== UI State ==========
  const setActiveTab = useCallback((tab: 'private' | 'groups') => {
    setState((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setState((prev) => ({ ...prev, searchQuery: query }));
  }, []);

  const searchUsers = useCallback(async (query: string) => {
    if (!query.trim()) {
      setState((prev) => ({ ...prev, searchResults: [] }));
      return;
    }
    try {
      const users = await messagingService.searchUsers(query);
      setState((prev) => ({ ...prev, searchResults: Array.isArray(users) ? users : [] }));
    } catch (error) {
      console.error('[MessagingContext] Failed to search users:', error);
      setState((prev) => ({ ...prev, searchResults: [] }));
    }
  }, []);

  // ========== Contacts ==========
  const fetchContacts = useCallback(async () => {
    try {
      const contacts = await messagingService.getContacts();
      setState((prev) => ({ ...prev, contacts: Array.isArray(contacts) ? contacts : [] }));
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch contacts:', error);
      setState((prev) => ({ ...prev, contacts: [] }));
    }
  }, []);

  const fetchContactRequests = useCallback(async () => {
    try {
      const contactRequests = await messagingService.getContactRequests();
      setState((prev) => ({ ...prev, contactRequests: Array.isArray(contactRequests) ? contactRequests : [] }));
    } catch (error) {
      console.error('[MessagingContext] Failed to fetch contact requests:', error);
      setState((prev) => ({ ...prev, contactRequests: [] }));
    }
  }, []);

  const sendContactRequest = useCallback(async (toUserId: number) => {
    const request = await messagingService.sendContactRequest({ to_user_id: toUserId });
    setState((prev) => {
      const currentRequests = Array.isArray(prev.contactRequests) ? prev.contactRequests : [];
      return {
        ...prev,
        contactRequests: [...currentRequests, request],
      };
    });
  }, []);

  const acceptContactRequest = useCallback(async (requestId: number) => {
    const contact = await messagingService.acceptContactRequest(requestId);
    setState((prev) => {
      const currentContacts = Array.isArray(prev.contacts) ? prev.contacts : [];
      const currentRequests = Array.isArray(prev.contactRequests) ? prev.contactRequests : [];
      return {
        ...prev,
        contacts: [...currentContacts, contact],
        contactRequests: currentRequests.filter((r) => r.id !== requestId),
      };
    });
  }, []);

  const rejectContactRequest = useCallback(async (requestId: number) => {
    await messagingService.rejectContactRequest(requestId);
    setState((prev) => {
      const currentRequests = Array.isArray(prev.contactRequests) ? prev.contactRequests : [];
      return {
        ...prev,
        contactRequests: currentRequests.filter((r) => r.id !== requestId),
      };
    });
  }, []);

  // ========== Context Value ==========
  const value: MessagingContextType = {
    state,
    fetchConversations,
    selectConversation,
    createConversation,
    deleteConversation,
    fetchMessages,
    sendMessage,
    sendMediaMessage,
    editMessage,
    deleteMessage,
    markAsRead,
    addReaction,
    removeReaction,
    fetchGroups,
    selectGroup,
    createGroup,
    updateGroup,
    deleteGroup,
    leaveGroup,
    addGroupMember,
    removeGroupMember,
    fetchGroupMessages,
    sendGroupMessage,
    sendGroupMediaMessage,
    editGroupMessage,
    deleteGroupMessage,
    markGroupAsRead,
    addGroupReaction,
    removeGroupReaction,
    sendTyping,
    setActiveTab,
    setSearchQuery,
    searchUsers,
    fetchContacts,
    fetchContactRequests,
    sendContactRequest,
    acceptContactRequest,
    rejectContactRequest,
    connect,
    disconnect,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

// ========== Custom Hook ==========
export const useMessaging = (): MessagingContextType => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export default MessagingContext;
